import { RcFile } from 'antd/lib/upload/interface';
import { getCdnUrl } from 'base/utils/trans-path';

// 图片压缩
type ICompressImageConfig = {
    file: RcFile | Blob;
    maxSize: number;
    forceToJPG: boolean;
    name: string;
    type: string;
};

function compressImage(config): Promise<any> {
    return new Promise(resolve => {
        const { file, maxSize, forceToJPG, name, type } = config;
        if (file.size <= maxSize) {
            return resolve(file);
        }

        // 图片压缩
        const reader = new FileReader();
        reader.readAsDataURL(file);
        reader.onload = function (e) {
            const img = document.createElement('img');
            img.src = reader.result as string;
            img.onload = () => {
                const canvas = document.createElement('canvas');
                const ctx = canvas.getContext('2d')!;
                const rate = file.size / maxSize;
                const width = Math.floor(img.naturalWidth / rate);
                const height = Math.floor(img.naturalHeight / rate);
                canvas.width = width;
                canvas.height = height;

                ctx.clearRect(0, 0, width, height);
                ctx.drawImage(img, 0, 0, width, height);

                // 转换格式
                let type = file.type;
                if (forceToJPG) {
                    file.name.replace(/\.\w+$/, '.jpg')
                }


                canvas.toBlob((blob) => {
                    // @ts-ignore
                    const file = new File([blob], name, {
                        type: 'image/jpeg',
                        lastModified: Date.now()
                    });
                    if (file.size > maxSize) {
                        resolve(compressImage({
                            file,
                            maxSize,
                            name,
                            type
                        }));
                    } else {
                        resolve(file as any)
                    }
                }, 'image/jpeg', 0.6);
            };
        };
    });
}

export function compressUploadImage(file: RcFile, maxSize: number, forceToJPG = true) {
    return compressImage({
        file,
        maxSize,
        forceToJPG,
        name: file.name,
        type: file.type,
    });
}

type IVideoInfo = {
    width: number;
    height: number;
    duration: number;
    cover: string;
};


/**
 * Todo: ios 无效没有触发 loadeddata， 加载必须用户手动交互
 * @param file
 */
export function getVideoInfo(file: RcFile): Promise<IVideoInfo> {
    return new Promise(function (resolve) {
        const video = document.createElement('video');
        video.autoplay = false;
        video.setAttribute('crossOrigin', 'anonymous');//处理跨域
        video.setAttribute('src', URL.createObjectURL(file));
        video.setAttribute('preload', 'metadata');
        video.setAttribute('muted', 'muted');
        video.addEventListener('loadeddata', function () {
            const width = video.videoWidth; //canvas的尺寸和图片一样
            const height = video.videoHeight;

            const canvas = document.createElement('canvas');
            canvas.width = width;
            canvas.height = height;
            canvas.getContext('2d')?.drawImage(video, 0, 0, width, height); //绘制canvas
            const cover = canvas.toDataURL('image/jpeg'); //转换为base64
            resolve({
                width,
                height,
                duration: video.duration,
                cover,
            });
        });
    })
}

export const isVideoPlaying = (video: HTMLVideoElement): boolean => {
    return !!(video.currentTime > 0 && !video.paused && !video.ended && video.readyState > 2);
}

/*
// 图片生成base64
function getImgBase64(url: string) {
    return new Promise(function (resolve, reject) {
        let Img = new Image();
        let dataURL = '';
        Img.src = url;
        Img.onload = function () { //确保图片完整获取
            var canvas = document.createElement('canvas'),//创建canvas元素
                width = Img.width, //canvas的尺寸和图片一样
                height = Img.height;
            canvas.width = width;
            canvas.height = height;
            canvas.getContext('2d')?.drawImage(Img, 0, 0, width, height);
            dataURL = canvas.toDataURL('image/jpeg'); //转换为base64
            resolve(dataURL);
        };
    });
}
*/

// https 图片加载
type ILoadConfig = {
    onSuccess?: () => void;
    onError?: () => void;
    onFinish?: () => void;
}

export function loadVideo(url: string, config?: ILoadConfig) {
    const { onSuccess, onError, onFinish } = config || {};
    const video = document.createElement('video');
    video.autoplay = false;
    video.setAttribute('crossOrigin', 'anonymous');//处理跨域
    video.setAttribute('src', url);
    video.setAttribute('preload', 'auto');
    video.setAttribute('muted', 'muted');
    video.addEventListener('loadeddata', function () {
        onSuccess?.();
        onFinish?.();
    });
    video.addEventListener('error', function () {
        onError?.();
        onFinish?.();
    });
}

export function loadImage(url: string, config?: ILoadConfig): Promise<HTMLImageElement | null> {
    return new Promise((resolve, reject) => {
        const { onSuccess, onError, onFinish } = config || {};
        let img = new Image()
        img.onload = function () {
            img.onload = null;
            onSuccess?.();
            onFinish?.();
            resolve(img);
        }
        img.onerror = function () {
            img.onerror = null;
            onError?.();
            onFinish?.();
            reject(url);
        }
        img.src = getCdnUrl(url);
    });


}

// 批量加载图片
type ILoadedImage = {
    image: HTMLImageElement | null;
    success: boolean;
}

export function loadImages(urls: string[]): Promise<Array<HTMLImageElement | null>> {
    return new Promise(resolve => {
        const images: Array<HTMLImageElement | null> = [];
        let loadedCounter = 0;

        const loadCllback = () => {
            if (loadedCounter === urls.length) {
                resolve(images)
            }
        };

        urls.forEach((url, index) => {
            loadImage(url).then(image => {
                images[index] = image;
                loadedCounter++;
                loadCllback();
            }).catch(() => {
                images[index] = null;
                loadedCounter++
                loadCllback();
            });
        });
    });
}

export function canvasDrawImage(canvas: HTMLCanvasElement, image: HTMLImageElement) {
    if (!canvas || !image) {
        return;
    }
    const ctx = canvas.getContext('2d');
    if (!ctx) {
        return;
    }
    let dw = image.width;
    let dh = image.height;
    const cw = canvas.offsetWidth;
    const ch = canvas.offsetHeight;
    canvas.width = cw;
    canvas.height = ch;

    //cover
    if (image.width > cw || image.height > ch) {
        if (image.width / image.height >= cw / ch) {
            dh = (image.height <= ch) ? image.height : ch;
            dw = Math.floor((dh * image.width) / image.height);
        } else {
            dw = (image.width <= cw) ? image.width : cw;
            dh = Math.floor((dw * image.height) / image.width);
        }
    }

    ctx.clearRect(0, 0, cw, ch);
    ctx.drawImage(image, (cw - dw) / 2, (ch - dh) / 2, dw, dh);
}